import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { MatTabsModule } from '@angular/material/tabs';
import { AppComponent } from './app.component';
import { MatTableModule } from '@angular/material/table';
import { LoginComponent } from './core/components/login/login.component';
import { NotRegisteredComponent } from './core/components/not-registered/not-registered.component';
import { PageNotFoundComponent } from './core/components/page-not-found/page-not-found.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { SecuredTokenInterceptor } from './core/interceptor/secured-token.interceptor';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { NgChartsModule } from 'ng2-charts';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { CustomerTrainingComponent } from './core/components/customer-training/customer-training.component';
import { GlobalErrorHandlerService } from './core/services/global-error-handler.service';
import { SecuredSkeletonModule } from './features/secured-skeleton/secured-skeleton.module';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { SnackbarComponent } from './core/components/snackbar/snackbar.component';
// import {
//   NguCarousel,
//   NguCarouselDefDirective,
//   NguCarouselNextDirective,
//   NguCarouselPrevDirective,
//   NguItemComponent,
//   NguTileComponent,
// } from '@ngu/carousel';
import './core/utils/date-extension';
import { DecimalPipe } from '@angular/common';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

// Configuration of the MSAL module
export function MSALInstanceFactory(): IPublicClientApplication {
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: environment.azureB2C.clientId,
      authority: `https://${environment.azureB2C.tenant}.b2clogin.com/${environment.azureB2C.tenant}.onmicrosoft.com/${environment.azureB2C.userFlows.signIn}`, // This seems to be part of a different flow
      redirectUri: environment.azureB2C.redirectUri,
      postLogoutRedirectUri: environment.azureB2C.postLogoutRedirectUri,
      knownAuthorities: [
        `https://${environment.azureB2C.tenant}.b2clogin.com/${environment.azureB2C.tenant}.onmicrosoft.com`,
        ...environment.azureB2C.otherKnownAutorities, // Add other authorities in case it is necessary
      ],
    },
    cache: {
      cacheLocation: environment.azureB2C.cacheLocation,
      storeAuthStateInCookie: environment.azureB2C.storeAuthStateInCookie,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
  return msalInstance;
}

export function initializeMsal(msalService: MsalService) {
  return () => msalService.instance.initialize();
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return environment.azureB2C.interceptorConfig;
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return environment.azureB2C.guardConfig;
}

const materialModules = [
  MatSnackBarModule,
  MatProgressSpinnerModule,
  MatTabsModule,
  MatTableModule,
  MatDialogModule,
  MatIconModule,
  MatSelectModule,
  MatButtonModule,
  MatIconModule,
];

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    NotRegisteredComponent,
    PageNotFoundComponent,
    CustomerTrainingComponent,
    SnackbarComponent,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    MsalModule,
    BrowserAnimationsModule,
    NgChartsModule,
    SecuredSkeletonModule,
    // NguCarousel,
    // NguCarouselDefDirective,
    // NguCarouselNextDirective,
    // NguCarouselPrevDirective,
    // NguItemComponent,
    // NguTileComponent,
    ...materialModules,
  ],
  providers: [
    provideAppInitializer(() => {
      const msalService = inject(MsalService);
      return msalService.instance.initialize();
    }),
    DecimalPipe,
    MsalBroadcastService,
    MsalService,
    MsalGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SecuredTokenInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        width: '800px',
      },
    },
    // Check if this will fire inside the secured module
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
